import type { SeatSocketStatus } from '../../ws/useSeatSocket.js';

/**
 * The six connection states (shell.md §Connection).
 *
 * The split that matters is **retryable versus terminal**. Four of these say
 * "wait"; two say "this token is dead and no amount of waiting fixes it". A
 * client that cannot tell them apart reconnects forever against a seat someone
 * else is now sitting in, which is what the repo did until this phase.
 */

export type LinkTone = 'ok' | 'warn' | 'bad';

export interface LinkView {
  /** The word in the status bar. */
  readonly token: string;
  readonly tone: LinkTone;
  /** Terminal states stop the backoff loop and route back to entry. */
  readonly terminal: boolean;
  /** True while the socket is trying again on its own. */
  readonly retrying: boolean;
  readonly latencyMs: number | null;
}

/** Above this a round trip is worth showing as degraded rather than nominal. */
export const LINK_DEGRADED_MS = 250;

/**
 * The two states no amount of waiting repairs.
 *
 * One definition, here rather than beside the socket, because the socket's job
 * is to *stop retrying* and the shell's is to *leave*, and those two decisions
 * drifting apart is how a console ends up looping quietly behind a screen that
 * says it has given up.
 */
export function isTerminalStatus(status: SeatSocketStatus): boolean {
  return status === 'superseded' || status === 'protocol-mismatch';
}

export function linkFor(status: SeatSocketStatus, roundTripMs: number | null): LinkView {
  switch (status) {
    case 'connecting':
      return {
        token: 'CONNECTING',
        tone: 'warn',
        terminal: false,
        retrying: false,
        latencyMs: null,
      };
    case 'reconnecting':
      return {
        token: 'RECONNECTING',
        tone: 'bad',
        terminal: false,
        retrying: true,
        latencyMs: null,
      };
    case 'resumed':
      return {
        token: 'RESUMED',
        tone: 'ok',
        terminal: false,
        retrying: false,
        latencyMs: roundTripMs,
      };
    case 'open':
      return {
        token: 'LIVE',
        tone: roundTripMs !== null && roundTripMs > LINK_DEGRADED_MS ? 'warn' : 'ok',
        terminal: false,
        retrying: false,
        latencyMs: roundTripMs,
      };
    case 'superseded':
      return {
        token: 'SUPERSEDED',
        tone: 'bad',
        terminal: isTerminalStatus(status),
        retrying: false,
        latencyMs: null,
      };
    case 'protocol-mismatch':
      return {
        token: 'VERSION',
        tone: 'bad',
        terminal: isTerminalStatus(status),
        retrying: false,
        latencyMs: null,
      };
    /*
     * `DROPPED`, not `OFFLINE`.
     *
     * `OFFLINE` is a rulebook asset state — it is one of NOMINAL / DEGRADED /
     * OFFLINE / DESTROYED, taught in the brief and rendered in the roster twenty
     * rows away in red. A cadet twenty minutes into their first exercise reading
     * `LINK · OFFLINE` has been given a word that already means something else
     * on the same screen.
     */
    case 'closed':
      return { token: 'DROPPED', tone: 'bad', terminal: false, retrying: false, latencyMs: null };
  }
}
